import type { Character } from '../domain/character.ts';
import type { RecurringDamage } from '../domain/encounter.ts';
import { createEncounter } from '../domain/encounter.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { SessionState } from '../domain/session.ts';
import type { Roller } from '../rules/dice.ts';
import { parseDice, rollDice } from '../rules/damageDice.ts';
import type { DamageRollResult } from '../rules/damageRoll.ts';
import type { CleanUpResult, EncounterActionTiming } from '../rules/encounter.ts';
import type { AttackExposure } from '../domain/encounter.ts';
import { ATTACK_EXPOSURES, isAttackExposure } from '../domain/encounter.ts';
import type { DeclarationChange } from '../rules/encounter.ts';
import {
  activeEffects,
  addIncomingAttack,
  advanceRound,
  beginEncounter,
  encounterActionProblem,
  endEncounter,
  removeTimedEffect,
  resolveTrigger,
  spendAction,
} from '../rules/encounter.ts';
import { advanceTime } from '../rules/gameTime.ts';
import { statCheckDifficulty } from '../rules/difficulty.ts';
import { manaRegenPerRound, restoreMana } from '../rules/mana.ts';
import { buildEffectiveCharacter } from './effectiveCharacter.ts';
import { deriveStats } from './derivedStats.ts';
import { addRolls } from '../rules/rollHistory.ts';
import type { RollHistoryEntry } from '../rules/rollHistory.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { ConditionContent } from '../content/conditions.ts';
import { takeDamage } from './healthService.ts';
import { recurringDamageHistoryEntry } from './historyEntries.ts';
import { performRoll } from './playSessionService.ts';
import { regeneratePetManaPerRound } from './petProgressionService.ts';

/**
 * Running an encounter.
 *
 * Everything here composes existing services. Recurring Debuff damage goes
 * through the SAME roll -> mitigation -> Health Bar path an attack uses
 * (`takeDamage`), and the automatic Con save goes through the SAME Check path a
 * player-initiated Stat Check uses (`performRoll`).
 */

export function startEncounter(runtime: RuntimeState, now: string): RuntimeState {
  // Active conditions and cooldowns carry into the fight untouched.
  return beginEncounter(runtime, createEncounter(runtime.characterId, now));
}

export type SpendActionOutcome =
  | { ok: true; runtime: RuntimeState; expiredEffectIds: string[] }
  | { ok: false; reason: string };

/**
 * The one pathway an Action is spent through.
 *
 * RULE DCC-ENC-003 - the caller must SAY whether this is an ordinary Action or
 * an Interrupt, and `timing` is deliberately required rather than defaulting to
 * `'normal'`. A default would let a future Interrupt be written that silently
 * spends on normal timing and is therefore legal in the wrong phase - the bug
 * would be an omission, and omissions do not show up in review.
 *
 * The phase check runs BEFORE the spend, so a wrong-phase Action costs nothing.
 *
 * RULE DCC-COND-009 - Staggered and Queasy end "At the end of the next Action
 * you take", and Shocked ends "Once you forfeit that Action" (Rules pp.45-46).
 * Firing the trigger HERE, after the spend, means it fires when an Action
 * actually completes. The preflight in `encounterActionProblem` must never do
 * it: an Effect that ended for an Action that was refused is an Effect the
 * crawler lost for nothing.
 */
export function spendEncounterAction(
  runtime: RuntimeState,
  actorId: string,
  count: number,
  timing: EncounterActionTiming,
): SpendActionOutcome {
  const problem = encounterActionProblem(runtime, actorId, count, timing);
  if (problem !== null) return { ok: false, reason: problem };

  const spent = spendAction(runtime, actorId, count);
  if (!spent.ok) return spent;
  const triggered = resolveTrigger(spent.runtime, 'after-next-action');
  return { ok: true, runtime: triggered.runtime, expiredEffectIds: triggered.expiredEffectIds };
}

/**
 * Rolls one Debuff's end-of-round damage.
 *
 * RULE DCC-ENC-004 - Rules p.45, Table 8. Burned is "1d10+F Fire damage at the
 * end of each round"; F is the Floor Number (Rules p.9).
 */
export function rollRecurringDamage(
  roller: Roller,
  damage: RecurringDamage,
  floor: number,
): DamageRollResult {
  const spec = parseDice(damage.dice);
  const dice = rollDice(roller, spec);

  const components = [
    { label: `${damage.dice}`, value: dice.total, faces: dice.faces },
    ...(damage.addFloor ? [{ label: `Floor Number (F${floor})`, value: floor }] : []),
  ];

  return {
    baseDiceRolled: spec,
    components,
    total: components.reduce((acc, c) => acc + c.value, 0),
    damageType: damage.damageType ?? 'untyped',
    criticalHit: false,
  };
}

export interface AdvanceRoundOutcome {
  character: Character;
  runtime: RuntimeState;
  session: SessionState;
  cleanUp: CleanUpResult;
  /** Readable lines for the panel, one per thing that happened. */
  lines: string[];
}

export interface AdvanceRoundArgs {
  now: string;
  newId: () => string;
  registry: ContentRegistry;
  /** Supplying this enables the fatal-damage save (Rules p.44). */
  conditions?: ConditionContent;
}

/**
 * RULE DCC-ENC-006 - declare one incoming attack.
 *
 * The application layer owns this rather than the rules layer because it mints
 * the id, and a pure function that reaches for a generator is not pure. The
 * ORDER is the point: legality is settled BEFORE `newId()` runs, so a
 * wrong-phase attempt cannot burn an id out of a stateful generator - the same
 * lesson the item-use preflight learned in B1r2.
 */
export function declareIncomingAttack(
  runtime: RuntimeState,
  input: { sourceLabel: string; evadeDifficulty: number; exposure: AttackExposure },
  deps: { newId: () => string },
): DeclarationChange {
  const encounter = runtime.encounter;
  if (!encounter) return { ok: false, reason: 'There is no encounter in progress.' };
  if (encounter.phase !== 'mob-declaration') {
    return {
      ok: false,
      reason: 'Incoming attacks are entered during Mob Declaration, and are settled after that.',
    };
  }
  const sourceLabel = input.sourceLabel.trim();
  if (sourceLabel === '') {
    return { ok: false, reason: 'An incoming attack needs a source, such as "Goblin 2 - Spear".' };
  }
  if (!Number.isInteger(input.evadeDifficulty) || input.evadeDifficulty < 1) {
    return { ok: false, reason: 'An Evade Difficulty is a whole number of at least 1.' };
  }
  // The TYPE says this is one of three values and today's `<select>` cannot
  // produce anything else, but this function is the request boundary and a type
  // is not a runtime guarantee. Minting an id for input the persisted validator
  // will reject afterwards is the same wasted-identity mistake as minting one
  // in the wrong phase.
  if (!isAttackExposure(input.exposure)) {
    return { ok: false, reason: `An incoming attack is ${ATTACK_EXPOSURES.join(', ')}.` };
  }

  // ONLY NOW.
  return addIncomingAttack(runtime, {
    id: deps.newId(),
    sourceLabel,
    evadeDifficulty: input.evadeDifficulty,
    exposure: input.exposure,
    // RULE DCC-ENC-007 - nothing has been rolled yet. A newly declared attack
    // is always unresolved; only the Evade Interrupt fills this in.
    evadeResult: null,
  });
}

/**
 * Thrown when a round is completed from a step that is not Step 4.
 *
 * Its own class so a caller can tell an illegal completion from a genuine
 * failure without matching on prose - the same reason `CheckpointRefusal` and
 * `RuntimeLoadRefusal` exist.
 */
export class RoundCompletionRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoundCompletionRefusal';
  }
}

/**
 * Combat Step 5: Clean Up, then the next round begins.
 *
 * THE ONLY ROUTE TO THE NEXT ROUND, and the legality boundary for it. RULE
 * DCC-ENC-002 puts Clean Up after Step 4, so this refuses unless the encounter
 * is in `crawler-action` - and it refuses FIRST, before a die is rolled, before
 * Mana moves, before Character, Session or Runtime is touched. Half of an
 * illegal round is worse than none of it.
 *
 * The check lives here rather than in the screen because the screen is not the
 * only possible caller, and a rule enforced only by a disabled button is a rule
 * that holds until someone adds a second button. `advanceRound` in the rules
 * layer stays a Clean Up primitive with no opinion about legality; this service
 * is where the opinion lives, and it has no other production caller.
 *
 * Order matters within it too: the damage a Debuff owes for the round now
 * ending is rolled and applied BEFORE the round advances, because the table
 * says "at the end of each round".
 */
export function advanceEncounterRound(
  roller: Roller,
  character: Character,
  runtime: RuntimeState,
  session: SessionState,
  args: AdvanceRoundArgs,
): AdvanceRoundOutcome {
  // BEFORE ANYTHING ELSE. Nothing below this point is reachable from an
  // illegal phase, which is what makes "refuses before mutation" a structural
  // fact rather than a claim about the order of the code.
  const current = runtime.encounter;
  if (!current) {
    throw new RoundCompletionRefusal('There is no encounter in progress to complete a round of.');
  }
  if (current.phase !== 'crawler-action') {
    throw new RoundCompletionRefusal(
      `A round is completed from the Crawler Action Phase, and this encounter is in ` +
        `${current.phase}. Advance the phase first.`,
    );
  }

  // RULE DCC-MANA-004 - SNAPSHOT THE RATE FIRST, before `advanceRound` expires
  // anything. The rule is "regenerate once per completed combat round based on
  // the crawler's effective Intelligence Modifier", and the round being
  // completed is the one an INT Buff was active FOR. Reading the modifier after
  // cleanup would consult a crawler the Buff has already left.
  //
  // Only the RATE is snapshotted. The cap is applied afterwards, against the
  // maximum that actually survives - see below.
  const regenAmount = manaRegenPerRound(
    buildEffectiveCharacter(character, activeEffects(runtime), (id) =>
      args.registry.getItem(id),
    ).statMod('int'),
  );

  const cleanUp = advanceRound(runtime);

  let updatedCharacter = character;
  let updatedRuntime = cleanUp.runtime;
  let updatedSession = session;
  const entries: RollHistoryEntry[] = [];
  const lines: string[] = [];

  for (const instruction of cleanUp.recurringDamage) {
    const rolled = rollRecurringDamage(roller, instruction.damage, updatedCharacter.floor);
    const effects = activeEffects(updatedRuntime);

    // RULE DCC-COND-007 - Rules p.44. If this damage would drop the crawler to
    // 0%, they make an automatic Con Stat Check vs 10 + Floor during Clean Up.
    if (args.conditions && wouldBeFatal(updatedCharacter, rolled.total, instruction, effects, (id) => args.registry.getItem(id))) {
      const difficulty = statCheckDifficulty(updatedCharacter.floor);

      // The SAME pathway a player-initiated Stat Check uses, so Effect Check
      // modifiers, Advantage sources, provenance and the after-next-check
      // trigger all behave identically. No Check arithmetic lives here.
      const save = performRoll(roller, {
        character: updatedCharacter,
        session: updatedSession,
        selection: { stat: 'con', skill: null },
        mode: 'normal',
        difficulty: difficulty.value,
        registry: args.registry,
        now: args.now,
        newId: args.newId,
        effects,
        runtime: updatedRuntime,
      });

      if (save.ok) {
        updatedSession = save.session;
        if (save.runtime) updatedRuntime = save.runtime;
        if (save.resolution?.success) {
          updatedRuntime = removeTimedEffect(updatedRuntime, instruction.effectId);
          lines.push(
            `${instruction.label}: Con Stat Check ${save.result.total} vs ${difficulty.value} SUCCEEDED. ` +
              'The Debuff ends and the damage is avoided (Rules p.44).',
          );
          continue;
        }
        lines.push(
          `${instruction.label}: Con Stat Check ${save.result.total} vs ${difficulty.value} failed.`,
        );
      }
    }

    // The ordinary damage pipeline: DR, resistance, vulnerability, immunity,
    // then whole Health Bar slots.
    const outcome = takeDamage(
      updatedCharacter,
      {
        amount: rolled.total,
        type: instruction.damage.damageType,
        ignoresDamageResistance: instruction.damage.ignoresDamageResistance,
      },
      effects,
      // RULE DCC-INV-002 - worn armour reduces recurring damage exactly as it
      // reduces any other damage. The catalogue is already on the args.
      (id) => args.registry.getItem(id),
    );
    updatedCharacter = outcome.character;

    entries.push(
      recurringDamageHistoryEntry({
        id: args.newId(),
        timestamp: args.now,
        label: instruction.label,
        sourceId: instruction.effectId,
        damage: rolled,
      }),
    );

    lines.push(
      `${instruction.label}: ${rolled.total} ${rolled.damageType} -> ` +
        `${outcome.mitigation.final} after mitigation, ` +
        `${outcome.healthBar.slotsLost} Health Bar slot${outcome.healthBar.slotsLost === 1 ? '' : 's'} lost`,
    );
  }

  // RULE DCC-MANA-004 - restore AFTER cleanup, capped against the maximum the
  // crawler actually ends the round with.
  //
  // The two halves come from different moments ON PURPOSE. An INT Buff that
  // expires during this Clean Up both raised the RATE (it was active for the
  // round) and raised MAX MANA (it no longer is). Capping against the pre-
  // cleanup maximum would leave Current Mana above Max the instant the Buff
  // left, which DCC-MANA-003 forbids. So: rate from before, cap from after.
  //
  // `restoreMana` does the capping, so there is no second Max Mana formula.
  // UNCONDITIONAL, and it stays that way. `restoreMana` computes
  // `min(max, current + regen)`, so on a round where a MAX-MANA Buff expires it
  // is what pulls Current Mana back down to the maximum that survived, which
  // DCC-MANA-003 requires. The live mistake to guard against is applying this
  // only when it RAISES Mana - `Math.max(current, restored)` or an
  // `if (change > 0)` around the assignment - because that skips exactly the
  // downward clamp. A clamp that lowers Current Mana is not negative
  // regeneration; it is the maximum being enforced after the Buff left, and the
  // two are reported differently below.
  //
  // (The old guard here was `if (regenAmount > 0)`. Since DCC-MANA-004 gained
  // its minimum of 1 that condition is always true, so it is no longer a
  // meaningful mutation - it is simply dead. The clamp is unconditional on its
  // own merits, not because regeneration can be zero.)
  {
    const after = deriveStats(updatedCharacter, activeEffects(updatedRuntime), (id) =>
      args.registry.getItem(id),
    );
    const restored = restoreMana(after.mana, regenAmount);
    if (restored.current !== updatedCharacter.currentMana) {
      const change = restored.current - updatedCharacter.currentMana;
      updatedCharacter = { ...updatedCharacter, currentMana: restored.current };
      // Two different events, said two different ways. "Mana regenerated: -3"
      // would be a lie about which one happened.
      lines.push(
        change > 0
          ? `Mana regenerated: ${change} (${restored.current}/${after.mana.max}).`
          : `Max Mana fell to ${after.mana.max}; Current Mana capped to ${restored.current}.`,
      );
    }

    const companions = updatedCharacter.companions.map((pet) => regeneratePetManaPerRound(pet, args.now));
    const petChanges = companions
      .map((pet, index) => ({ pet, before: updatedCharacter.companions[index]! }))
      .filter(({ pet, before }) => pet.currentMana !== before.currentMana);
    if (petChanges.length > 0) {
      updatedCharacter = { ...updatedCharacter, companions };
      for (const { pet, before } of petChanges) {
        lines.push(`${pet.name} Mana regenerated: ${pet.currentMana - before.currentMana} (${pet.currentMana}).`);
      }
    }
  }

  for (const effectId of cleanUp.expiredEffectIds) lines.push(`${effectId} expired`);
  for (const sourceId of cleanUp.endedCooldownIds) lines.push(`${sourceId} is off cooldown`);
  if (updatedRuntime.encounter) {
    lines.push(`Round ${updatedRuntime.encounter.round} begins. Actions reset.`);
  }

  return {
    character: updatedCharacter,
    runtime: updatedRuntime,
    session: { ...updatedSession, rollHistory: addRolls(updatedSession.rollHistory, entries) },
    cleanUp,
    lines,
  };
}

/**
 * Time passing outside a fight.
 *
 * The Play Sheet's pass-time control, and the one pathway for "ten minutes go
 * by" that is not a round and not a rest. It uses the SAME `advanceTime`
 * primitive as both of those, so a Debuff cannot expire one way and not
 * another.
 *
 * No damage is rolled. Recurring damage is owed at the end of a combat ROUND
 * (Rules p.45, Table 8), and nothing here is a round: time passing outside a
 * fight is not the crawler standing in a fire taking turns.
 */
export interface PassTimeOutcome {
  runtime: RuntimeState;
  expiredEffectIds: string[];
  endedCooldownIds: string[];
}

export function passTime(runtime: RuntimeState, seconds: number): PassTimeOutcome {
  const result = advanceTime(runtime, seconds);
  return {
    runtime: result.runtime,
    expiredEffectIds: result.expiredEffectIds,
    endedCooldownIds: result.endedCooldownIds,
  };
}

export interface EndEncounterOutcome {
  runtime: RuntimeState;
  lines: string[];
}

/**
 * Ends the encounter.
 *
 * Conditions and cooldowns that outlive a fight REMAIN on the runtime state and
 * are reported, so the player can see what carried over.
 */
export function endEncounterWithReport(runtime: RuntimeState): EndEncounterOutcome {
  const result = endEncounter(runtime);
  const lines: string[] = [];

  for (const id of result.expiredEffectIds) lines.push(`${id} ended with the encounter`);
  for (const id of result.endedCooldownIds) lines.push(`${id} is off cooldown`);
  for (const record of result.runtime.active) {
    lines.push(`${record.effect.label} continues after combat`);
  }
  for (const cooldown of result.runtime.cooldowns) {
    lines.push(`${cooldown.sourceId} remains on cooldown (${cooldown.scope})`);
  }
  lines.push('Encounter ended.');
  return { runtime: result.runtime, lines };
}

/** Would this Debuff's damage, after mitigation, empty the Health Bar? */
function wouldBeFatal(
  character: Character,
  amount: number,
  instruction: { damage: RecurringDamage },
  effects: readonly import('../rules/effects.ts').Effect[],
  lookupItem?: (itemId: string) => import('../content/types.ts').Item | undefined,
): boolean {
  // Rolled against the real pipeline rather than estimated, so the save fires
  // exactly when the damage would actually be fatal.
  const outcome = takeDamage(
    character,
    {
      amount,
      type: instruction.damage.damageType,
      ignoresDamageResistance: instruction.damage.ignoresDamageResistance,
    },
    effects,
    lookupItem,
  );
  return outcome.character.healthBarSlotsRemaining === 0 && character.healthBarSlotsRemaining > 0;
}
